import React, { useRef, useState, useEffect } from 'react';
import { Loader2, Upload, X } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { AsyncSearchableSelect } from '../ui/async-searchable-select';
import { SearchableSelect } from '../ui/searchable-select';
import { toast } from 'sonner';
import { directoryApi, uploadsApi } from '../../services/api';

const mapPortToOption = (p) => ({
  value: p.code,
  label: p.nameAndLocode || `${p.name} (${p.code})`,
  country: p.country,
});
const DIMENSION_UNIT_OPTIONS = [
  { value: 'mm', label: 'mm' },
  { value: 'cm', label: 'cm' },
  { value: 'inch', label: 'Inch' },
  { value: 'm', label: 'Meter' },
];
const CONTAINER_SHARING_OPTIONS = [
  { value: 'acceptable', label: 'Acceptable - Can share container' },
  { value: 'preferred_no', label: 'Prefer not to share' },
  { value: 'required_no', label: 'Exclusive use required' },
];
const HAZ_CLASS_OPTIONS = [
  'Class 1 - Explosives',
  'Class 2 - Gases',
  'Class 3 - Flammable Liquids',
  'Class 4 - Flammable Solids',
  'Class 5 - Oxidizing Substances',
  'Class 6 - Toxic Substances',
  'Class 7 - Radioactive Material',
  'Class 8 - Corrosives',
  'Class 9 - Miscellaneous'
];
const MAX_MSDS_FILES = 5;
const MAX_MSDS_FILE_SIZE_MB = 5;
const MAX_MSDS_FILE_SIZE_BYTES = MAX_MSDS_FILE_SIZE_MB * 1024 * 1024;

const createEmptyDimensionRow = () => ({
  pieces: '',
  length: '',
  width: '',
  height: '',
  unit: 'cm',
  stackability: ''
});

const toInt = (value) => {
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const getTotalPiecesFromRows = (rows = []) =>
  (Array.isArray(rows) ? rows : []).reduce((sum, row) => sum + toInt(row?.pieces), 0);

const createEmptyCargoLine = () => ({
  description: '',
  hsCode: '',
  weight: '',
  volume: '',
  pieces: 0,
  dimensionsRows: [createEmptyDimensionRow()]
});

const normalizeCargoLine = (line = {}, legacyFormStackability = '') => {
  const legacyDimensions = line?.dimensions || {};
  const rawRows = Array.isArray(line?.dimensionsRows) && line.dimensionsRows.length > 0
    ? line.dimensionsRows
    : [{
      pieces: line?.pieces ?? '',
      length: legacyDimensions?.length ?? '',
      width: legacyDimensions?.width ?? '',
      height: legacyDimensions?.height ?? '',
      unit: legacyDimensions?.unit ?? 'cm'
    }];

  const dimensionsRows = rawRows.map((row) => ({
    pieces: row?.pieces ?? '',
    length: row?.length ?? '',
    width: row?.width ?? '',
    height: row?.height ?? '',
    unit: row?.unit ?? 'cm',
    stackability: row?.stackability || legacyFormStackability || ''
  }));

  return {
    ...createEmptyCargoLine(),
    ...line,
    dimensionsRows,
    pieces: getTotalPiecesFromRows(dimensionsRows)
  };
};
const DEFAULT_LCL_FORM = {
  scope: [],
  pol: '', polCountry: '', polName: '',
  pod: '', podCountry: '', podName: '',
  por: '', porCountry: '', porName: '',
  fpod: '', fpodCountry: '', fpodName: '',
  incoterm: '',
  commodity: '',
  cargoDetails: [
    createEmptyCargoLine()
  ],
  totalWeight: 0,
  totalVolume: 0,
  isHazardous: false,
  hazClass: '',
  unNumber: '',
  msdsFiles: [],
  specialHandling: [],
  fragileHandling: 'all',
  fragileBoxCount: '',
  temperatureRange: '',
  containerSharing: 'acceptable',
  packagingType: 'Cartons',
  polFreeTime: '',
  podFreeTime: '',
  insuranceRequired: false,
  customsValue: '',
  specialRequirements: ''
};

const LCLForm = ({ onSubmit, onCancel, oceanPorts = [], incoterms = [], commodities = [], initialData = null, formId = null, scopeOptions: scopeOptionsProp, onFormDataChange, validationErrors = {}, clearValidationError }) => {
  const [formData, setFormData] = useState(() => {
    if (initialData && typeof initialData === 'object') {
      const legacyFormStackability = initialData.stackability || '';
      const { stackability: _removed, ...restInitial } = initialData;
      const normalizedIncoterm = Array.isArray(restInitial.incoterm)
        ? (restInitial.incoterm[0] || '')
        : (restInitial.incoterm || '');
      const cargoDetails = Array.isArray(restInitial.cargoDetails) && restInitial.cargoDetails.length > 0
        ? restInitial.cargoDetails.map((line) => normalizeCargoLine(line, legacyFormStackability))
        : [normalizeCargoLine({}, legacyFormStackability)];
      return { ...DEFAULT_LCL_FORM, ...restInitial, incoterm: normalizedIncoterm, cargoDetails };
    }
    return { ...DEFAULT_LCL_FORM };
  });
  const [portLabels, setPortLabels] = useState({ por: '', pol: '', pod: '', fpod: '' });
  const [uploadingMsds, setUploadingMsds] = useState(false);
  const msdsInputRef = useRef(null);

  useEffect(() => {
    const fetchLabel = (field, code, apiByCode) => {
      if (!code) return;
      apiByCode(code).then((res) => {
        const item = res.data;
        const label = item?.nameAndLocode || (item?.name && item?.code ? `${item.name} (${item.code})` : code);
        setPortLabels((prev) => ({ ...prev, [field]: label }));
      }).catch(() => setPortLabels((prev) => ({ ...prev, [field]: code })));
    };
    if (formData.por) fetchLabel('por', formData.por, directoryApi.getPorFpodByCode);
    if (formData.pol) fetchLabel('pol', formData.pol, directoryApi.getPortByCode);
    if (formData.pod) fetchLabel('pod', formData.pod, directoryApi.getPortByCode);
    if (formData.fpod) fetchLabel('fpod', formData.fpod, directoryApi.getPorFpodByCode);
  }, [formData.por, formData.pol, formData.pod, formData.fpod]);

  useEffect(() => {
    onFormDataChange?.(formData);
  }, [formData, onFormDataChange]);

  const DEFAULT_LCL_SCOPE = ['Origin Inland (LCL)', 'Origin CFS / Warehouse (LCL)', 'Origin Customs (LCL)', 'Origin Terminal (LCL)', 'Ocean Freight (LCL)', 'Destination CFS (LCL)', 'Destination Customs (LCL)', 'Destination Inland (LCL)', 'Special Cargo / Value Added'];
  const scopeOptions = (scopeOptionsProp && scopeOptionsProp.length > 0) ? scopeOptionsProp : DEFAULT_LCL_SCOPE;
  const specialHandlingOptions = ['Fragile', 'Keep Dry', 'This Side Up', 'Stack Limit', 'Temperature Sensitive'];
  const packagingTypes = ['Cartons', 'Crates', 'Pallets', 'Drums', 'Bags', 'Other'];
  const packagingTypeOptions = packagingTypes.map((type) => ({ value: type, label: type }));
  const hazClassOptions = HAZ_CLASS_OPTIONS.map((haz) => ({ value: haz, label: haz }));
  const commodityOptions = commodities.map((comm) => {
    const value = typeof comm === 'object' ? comm.name || String(comm) : comm;
    return { value, label: value };
  });

  const addCargoLine = () => {
    const previousLine = formData.cargoDetails[formData.cargoDetails.length - 1];
    const nextLine = previousLine
      ? {
        ...previousLine,
        dimensionsRows: (previousLine.dimensionsRows || []).map((row) => ({ ...row }))
      }
      : createEmptyCargoLine();

    setFormData({
      ...formData,
      cargoDetails: [...formData.cargoDetails, nextLine]
    });
  };

  const removeCargoLine = (index) => {
    const newDetails = formData.cargoDetails.filter((_, i) => i !== index);
    setFormData({ ...formData, cargoDetails: newDetails });
  };

  const updateCargoLine = (index, field, value) => {
    const newDetails = [...formData.cargoDetails];
    newDetails[index][field] = value;
    setFormData({ ...formData, cargoDetails: newDetails });
  };

  const addDimensionRow = (cargoIndex) => {
    const newDetails = [...formData.cargoDetails];
    const cargo = { ...newDetails[cargoIndex] };
    const existingRows = Array.isArray(cargo.dimensionsRows) ? cargo.dimensionsRows : [];
    cargo.dimensionsRows = [...existingRows, createEmptyDimensionRow()];
    cargo.pieces = getTotalPiecesFromRows(cargo.dimensionsRows);
    newDetails[cargoIndex] = cargo;
    setFormData({ ...formData, cargoDetails: newDetails });
  };

  const removeDimensionRow = (cargoIndex, rowIndex) => {
    const newDetails = [...formData.cargoDetails];
    const cargo = { ...newDetails[cargoIndex] };
    const existingRows = Array.isArray(cargo.dimensionsRows) ? cargo.dimensionsRows : [];
    const filteredRows = existingRows.filter((_, i) => i !== rowIndex);
    cargo.dimensionsRows = filteredRows.length > 0 ? filteredRows : [createEmptyDimensionRow()];
    cargo.pieces = getTotalPiecesFromRows(cargo.dimensionsRows);
    newDetails[cargoIndex] = cargo;
    setFormData({ ...formData, cargoDetails: newDetails });
  };

  const updateDimensionRow = (cargoIndex, rowIndex, field, value) => {
    const newDetails = [...formData.cargoDetails];
    const cargo = { ...newDetails[cargoIndex] };
    const existingRows = Array.isArray(cargo.dimensionsRows) ? cargo.dimensionsRows : [createEmptyDimensionRow()];
    const rows = existingRows.map((row, idx) => (idx === rowIndex ? { ...row, [field]: value } : row));
    cargo.dimensionsRows = rows;
    cargo.pieces = getTotalPiecesFromRows(rows);
    newDetails[cargoIndex] = cargo;
    setFormData({ ...formData, cargoDetails: newDetails });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleMsdsUpload = async (event) => {
    const fileList = Array.from(event.target.files || []);
    if (!fileList.length) return;

    const currentFiles = Array.isArray(formData.msdsFiles) ? formData.msdsFiles : [];
    if (currentFiles.length + fileList.length > MAX_MSDS_FILES) {
      toast.error(`Maximum ${MAX_MSDS_FILES} MSDS files allowed.`);
      event.target.value = '';
      return;
    }

    for (const file of fileList) {
      if (file.size > MAX_MSDS_FILE_SIZE_BYTES) {
        toast.error(`File "${file.name}" exceeds ${MAX_MSDS_FILE_SIZE_MB}MB.`);
        event.target.value = '';
        return;
      }
    }

    setUploadingMsds(true);
    try {
      const uploaded = [];
      for (const file of fileList) {
        const res = await uploadsApi.uploadFile(file, 'quote_document');
        const url = res?.data?.file?.url || res?.data?.file_url || res?.data?.url;
        const name = res?.data?.file?.original_filename || res?.data?.original_filename || file.name;
        const size = res?.data?.file?.size || file.size;
        if (url) {
          uploaded.push({
            name,
            url,
            size
          });
        }
      }

      if (uploaded.length > 0) {
        setFormData((prev) => ({
          ...prev,
          msdsFiles: [...(prev.msdsFiles || []), ...uploaded]
        }));
        toast.success(`${uploaded.length} MSDS file(s) uploaded.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload MSDS file(s).');
    } finally {
      setUploadingMsds(false);
      event.target.value = '';
    }
  };

  const removeMsdsFile = (index) => {
    setFormData((prev) => ({
      ...prev,
      msdsFiles: (prev.msdsFiles || []).filter((_, i) => i !== index)
    }));
  };

  return (
    <form id={formId} onSubmit={handleSubmit} className="space-y-6" noValidate>
      {/* Scope */}
      <div>
        <Label>Scope (Select all that apply)</Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
          {scopeOptions.map((scope) => (
            <label key={scope} className="flex items-center space-x-2 p-2 border rounded cursor-pointer hover:bg-gray-50">
              <input
                type="checkbox"
                checked={formData.scope.includes(scope)}
                onChange={(e) => {
                  const newScopes = e.target.checked
                    ? [...formData.scope, scope]
                    : formData.scope.filter(s => s !== scope);
                  setFormData({ ...formData, scope: newScopes });
                  onFormDataChange?.({ scope: newScopes });
                  clearValidationError?.('scope');
                }}
                className="rounded"
              />
              <span className="text-sm">{scope}</span>
            </label>
          ))}
        </div>
        {validationErrors.scope && <p className="mt-1 text-sm text-red-600">{validationErrors.scope}</p>}
      </div>

      {/* Ports */}
      <div className="border-t pt-4">
        <h3 className="font-semibold text-lg mb-4">Port Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>POR (Place of Receipt)</Label>
            <AsyncSearchableSelect
              fetchOptions={async (q, lim) => {
                const res = await directoryApi.searchPorFpod(q, lim);
                return (res.data || []).map(mapPortToOption);
              }}
              value={formData.por}
              selectedLabel={portLabels.por}
              onChange={(value, item) => {
                setFormData({ ...formData, por: value, porCountry: item?.country || '', porName: item?.label || value });
                setPortLabels((prev) => ({ ...prev, por: item?.label || value }));
                if (value) clearValidationError?.('por');
              }}
              placeholder="Select Port..."
              className="w-full"
              error={Boolean(validationErrors.por)}
            />
            {validationErrors.por && <p className="mt-1 text-sm text-red-600">{validationErrors.por}</p>}
          </div>
          <div>
            <Label>POL (Port of Loading)</Label>
            <AsyncSearchableSelect
              fetchOptions={async (q, lim) => {
                const res = await directoryApi.searchPorts(q, lim);
                return (res.data || []).map(mapPortToOption);
              }}
              value={formData.pol}
              selectedLabel={portLabels.pol}
              onChange={(value, item) => {
                setFormData({ ...formData, pol: value, polCountry: item?.country || '', polName: item?.label || value });
                setPortLabels((prev) => ({ ...prev, pol: item?.label || value }));
                clearValidationError?.('pol');
              }}
              placeholder="Select Port..."
              className="w-full"
              error={Boolean(validationErrors.pol)}
            />
            {validationErrors.pol && <p className="mt-1 text-sm text-red-600">{validationErrors.pol}</p>}
          </div>
          <div>
            <Label>POD (Port of Discharge)</Label>
            <AsyncSearchableSelect
              fetchOptions={async (q, lim) => {
                const res = await directoryApi.searchPorts(q, lim);
                return (res.data || []).map(mapPortToOption);
              }}
              value={formData.pod}
              selectedLabel={portLabels.pod}
              onChange={(value, item) => {
                setFormData({ ...formData, pod: value, podCountry: item?.country || '', podName: item?.label || value });
                setPortLabels((prev) => ({ ...prev, pod: item?.label || value }));
                clearValidationError?.('pod');
              }}
              placeholder="Select Port..."
              className="w-full"
              error={Boolean(validationErrors.pod)}
            />
            {validationErrors.pod && <p className="mt-1 text-sm text-red-600">{validationErrors.pod}</p>}
          </div>
          <div>
            <Label>FPOD (Final Place of Delivery)</Label>
            <AsyncSearchableSelect
              fetchOptions={async (q, lim) => {
                const res = await directoryApi.searchPorFpod(q, lim);
                return (res.data || []).map(mapPortToOption);
              }}
              value={formData.fpod}
              selectedLabel={portLabels.fpod}
              onChange={(value, item) => {
                setFormData({ ...formData, fpod: value, fpodCountry: item?.country || '', fpodName: item?.label || value });
                setPortLabels((prev) => ({ ...prev, fpod: item?.label || value }));
                if (value) clearValidationError?.('fpod');
              }}
              placeholder="Select Port..."
              className="w-full"
              error={Boolean(validationErrors.fpod)}
            />
            {validationErrors.fpod && <p className="mt-1 text-sm text-red-600">{validationErrors.fpod}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <div>
            <Label>Incoterm</Label>
            <SearchableSelect
              options={incoterms.map((term) => {
                const value = typeof term === 'object' ? term.code : term;
                const label = typeof term === 'object' ? `${term.code} - ${term.name}` : term;
                return { value, label };
              })}
              value={formData.incoterm}
              onChange={(value) => {
                setFormData({ ...formData, incoterm: value || '' });
                if (value) clearValidationError?.('incoterm');
              }}
              placeholder="Search or select incoterm"
              allowOther={false}
              error={Boolean(validationErrors.incoterm)}
            />
            {validationErrors.incoterm && <p className="mt-1 text-sm text-red-600">{validationErrors.incoterm}</p>}
          </div>
          <div>
            <Label>POL Free Time (days)</Label>
            <Input
              type="number"
              min="0"
              value={formData.polFreeTime}
              onChange={(e) => setFormData({ ...formData, polFreeTime: Math.max(0, e.target.value) })}
              onInput={(e) => {
                if (e.target.value !== '') clearValidationError?.('polFreeTime');
              }}
              placeholder="e.g., 7"
              className={validationErrors.polFreeTime ? 'border-red-500' : ''}
              required
            />
            {validationErrors.polFreeTime && <p className="mt-1 text-sm text-red-600">{validationErrors.polFreeTime}</p>}
          </div>
          <div>
            <Label>POD Free Time (days)</Label>
            <Input
              type="number"
              min="0"
              value={formData.podFreeTime}
              onChange={(e) => setFormData({ ...formData, podFreeTime: Math.max(0, e.target.value) })}
              onInput={(e) => {
                if (e.target.value !== '') clearValidationError?.('podFreeTime');
              }}
              placeholder="e.g., 7"
              className={validationErrors.podFreeTime ? 'border-red-500' : ''}
              required
            />
            {validationErrors.podFreeTime && <p className="mt-1 text-sm text-red-600">{validationErrors.podFreeTime}</p>}
          </div>
        </div>
      </div>

      {/* Cargo Details */}
      <div className="border-t pt-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-lg">Cargo Details</h3>
          <button
            type="button"
            onClick={addCargoLine}
            className="px-3 py-1 text-sm bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
          >
            + Add Line
          </button>
        </div>

        {validationErrors.cargoDetails && <p className="mb-3 text-sm text-red-600">{validationErrors.cargoDetails}</p>}
        {formData.cargoDetails.map((cargo, index) => (
          <div key={index} className="mb-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">Item {index + 1}</span>
              {formData.cargoDetails.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeCargoLine(index)}
                  className="text-red-600 text-sm hover:text-red-700"
                >
                  Remove
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Label className="text-xs">Description</Label>
                <Input
                  value={cargo.description}
                  onChange={(e) => updateCargoLine(index, 'description', e.target.value)}
                  onInput={(e) => {
                    if (e.target.value?.trim()) clearValidationError?.('cargoDetails');
                  }}
                  placeholder="Cargo description"
                  required
                />
              </div>
              <div>
                <Label className="text-xs">HS Code</Label>
                <Input
                  value={cargo.hsCode}
                  onChange={(e) => updateCargoLine(index, 'hsCode', e.target.value)}
                  placeholder="6-10 digits"
                />
              </div>
              <div>
                <Label className="text-xs">Number of Pieces</Label>
                <Input
                  type="number"
                  value={cargo.pieces}
                  placeholder="0"
                  readOnly
                  disabled
                />
              </div>
              <div>
                <Label className="text-xs">Weight (kg)</Label>
                <Input
                  type="number"
                  value={cargo.weight}
                  onChange={(e) => updateCargoLine(index, 'weight', e.target.value)}
                  placeholder="0"
                  required
                />
              </div>
              <div>
                <Label className="text-xs">Volume (m³)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={cargo.volume}
                  onChange={(e) => updateCargoLine(index, 'volume', e.target.value)}
                  placeholder="0.00"
                  required
                />
              </div>
            </div>

            <div className="mt-3">
              <Label className="text-xs">Dimensions per Piece <span className="text-red-500">*</span></Label>
              <div className="space-y-2 mt-1">
                {(cargo.dimensionsRows || [createEmptyDimensionRow()]).map((row, rowIndex) => (
                  <div key={rowIndex} className="rounded-md border border-gray-200 bg-white p-3 space-y-2">
                    <div className="flex flex-wrap items-end gap-2">
                      <SearchableSelect
                        options={DIMENSION_UNIT_OPTIONS}
                        className="w-24 shrink-0"
                        value={row?.unit ?? 'cm'}
                        onChange={(value) => updateDimensionRow(index, rowIndex, 'unit', value || 'cm')}
                        placeholder="Unit"
                        allowOther={false}
                      />
                      <Input
                        type="number"
                        className="flex-1 min-w-[6rem]"
                        value={row?.pieces ?? ''}
                        onChange={(e) => updateDimensionRow(index, rowIndex, 'pieces', e.target.value)}
                        placeholder="No. of Pieces"
                      />
                      <Input
                        type="number"
                        className="flex-1 min-w-[4rem]"
                        value={row?.length ?? ''}
                        onChange={(e) => updateDimensionRow(index, rowIndex, 'length', e.target.value)}
                        placeholder="Length"
                      />
                      <Input
                        type="number"
                        className="flex-1 min-w-[4rem]"
                        value={row?.width ?? ''}
                        onChange={(e) => updateDimensionRow(index, rowIndex, 'width', e.target.value)}
                        placeholder="Width"
                      />
                      <Input
                        type="number"
                        className="flex-1 min-w-[4rem]"
                        value={row?.height ?? ''}
                        onChange={(e) => updateDimensionRow(index, rowIndex, 'height', e.target.value)}
                        placeholder="Height"
                      />
                      {(cargo.dimensionsRows || []).length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeDimensionRow(index, rowIndex)}
                          className="px-2 py-2 text-xs text-red-600 hover:text-red-700"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-xs">
                      <span className="text-gray-600 font-medium">Stackability</span>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="radio"
                          name={`dim-stack-${index}-${rowIndex}`}
                          checked={row?.stackability === 'stackable'}
                          onChange={() => updateDimensionRow(index, rowIndex, 'stackability', 'stackable')}
                        />
                        Stackable
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="radio"
                          name={`dim-stack-${index}-${rowIndex}`}
                          checked={row?.stackability === 'non_stackable'}
                          onChange={() => updateDimensionRow(index, rowIndex, 'stackability', 'non_stackable')}
                        />
                        Non-stackable
                      </label>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addDimensionRow(index)}
                  className="px-2 py-1 text-xs bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                >
                  + Add
                </button>
              </div>
            </div>
          </div>
        ))}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <Label>Commodity Type</Label>
            <SearchableSelect
              options={commodityOptions}
              value={formData.commodity}
              onChange={(value) => {
                setFormData({ ...formData, commodity: value || '' });
                if (value) clearValidationError?.('commodity');
              }}
              placeholder="Search or select commodity"
              allowOther={false}
              error={!!validationErrors.commodity}
            />
            {validationErrors.commodity && <p className="mt-1 text-sm text-red-600">{validationErrors.commodity}</p>}
          </div>
          <div>
            <Label>Packaging Type</Label>
            <SearchableSelect
              options={packagingTypeOptions}
              value={formData.packagingType}
              onChange={(value) => setFormData({ ...formData, packagingType: value || 'Cartons' })}
              placeholder="Search or select packaging type"
              allowOther={false}
            />
          </div>
        </div>
      </div>

      {/* Special Handling */}
      <div className="border-t pt-4">
        <h3 className="font-semibold text-lg mb-4">Special Requirements</h3>

        <div>
          <Label>Container Sharing Preference</Label>
          <SearchableSelect
            options={CONTAINER_SHARING_OPTIONS}
            value={formData.containerSharing}
            onChange={(value) => setFormData({ ...formData, containerSharing: value || 'acceptable' })}
            placeholder="Search or select container sharing"
            allowOther={false}
          />
        </div>

        <div className="mt-4">
          <Label>Special Handling</Label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
            {specialHandlingOptions.map((option) => (
              <label key={option} className="flex items-center space-x-2 p-2 border rounded cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={formData.specialHandling.includes(option)}
                  onChange={(e) => {
                    const newHandling = e.target.checked
                      ? [...formData.specialHandling, option]
                      : formData.specialHandling.filter(h => h !== option);
                    const next = { ...formData, specialHandling: newHandling };
                    if (option === 'Fragile' && !e.target.checked) {
                      next.fragileHandling = 'all';
                      next.fragileBoxCount = '';
                    }
                    if (option === 'Temperature Sensitive' && !e.target.checked) next.temperatureRange = '';
                    setFormData(next);
                  }}
                  className="rounded"
                />
                <span className="text-sm">{option}</span>
              </label>
            ))}
          </div>
        </div>

        {formData.specialHandling.includes('Fragile') && (
          <div className="mt-4 p-3 border rounded-md bg-gray-50">
            <Label className="text-sm">Fragile handling applies to</Label>
            <div className="mt-2 space-y-2">
              <label className="flex items-center space-x-2">
                <input
                  type="radio"
                  name="fragileHandling"
                  checked={formData.fragileHandling === 'all'}
                  onChange={() => setFormData({ ...formData, fragileHandling: 'all', fragileBoxCount: '' })}
                />
                <span className="text-sm">All cargo boxes</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="radio"
                  name="fragileHandling"
                  checked={formData.fragileHandling === 'specific'}
                  onChange={() => setFormData({ ...formData, fragileHandling: 'specific' })}
                />
                <span className="text-sm">Specific number of boxes</span>
              </label>
              {formData.fragileHandling === 'specific' && (
                <div className="max-w-md">
                  <Input
                    type="text"
                    autoComplete="off"
                    value={formData.fragileBoxCount}
                    onChange={(e) => setFormData({ ...formData, fragileBoxCount: e.target.value })}
                    placeholder="e.g. 12 or brief description"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {formData.specialHandling.includes('Temperature Sensitive') && (
          <div className="mt-4">
            <Label htmlFor="temperatureRange">Temperature range required</Label>
            <Input
              id="temperatureRange"
              value={formData.temperatureRange ?? ''}
              onChange={(e) => setFormData({ ...formData, temperatureRange: e.target.value })}
              placeholder="e.g., 2°C to 8°C or -18°C to -15°C"
              className="mt-1 max-w-md"
            />
          </div>
        )}

        <div className="mt-4">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={formData.isHazardous}
              onChange={(e) => setFormData({ ...formData, isHazardous: e.target.checked })}
              className="rounded"
            />
            <span className="text-sm font-medium">Hazardous Material</span>
          </label>
        </div>

        {formData.isHazardous && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 p-4 bg-red-50 rounded-lg">
            <div>
              <Label>UN Number</Label>
              <Input
                value={formData.unNumber}
                onChange={(e) => setFormData({ ...formData, unNumber: e.target.value })}
                placeholder="e.g., UN1234"
                required={formData.isHazardous}
              />
            </div>
            <div>
              <Label>Hazard Class</Label>
              <SearchableSelect
                options={hazClassOptions}
                value={formData.hazClass}
                onChange={(value) => setFormData({ ...formData, hazClass: value || '' })}
                placeholder="Search or select hazard class"
                allowOther={false}
              />
            </div>
            <div className="md:col-span-2">
              <Label className="block mb-2">MSDS Documents (max {MAX_MSDS_FILES} files, {MAX_MSDS_FILE_SIZE_MB}MB each)</Label>
              <div className="flex items-center gap-3 flex-wrap">
                <Input
                  ref={msdsInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx"
                  multiple
                  className="hidden"
                  onChange={handleMsdsUpload}
                />
                <button
                  type="button"
                  onClick={() => msdsInputRef.current?.click()}
                  disabled={uploadingMsds || (formData.msdsFiles || []).length >= MAX_MSDS_FILES}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md border text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploadingMsds ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {uploadingMsds ? 'Uploading...' : ((formData.msdsFiles || []).length >= MAX_MSDS_FILES ? 'Max files reached' : 'Upload MSDS')}
                </button>
                <span className="text-xs text-gray-500">
                  Upload up to {MAX_MSDS_FILES} documents. Accepted: PDF, DOC, DOCX.
                </span>
                <span className="text-xs font-medium text-gray-700">
                  {(formData.msdsFiles || []).length} / {MAX_MSDS_FILES} files selected
                </span>
              </div>
              {(formData.msdsFiles || []).length > 0 && (
                <div className="mt-3 space-y-2">
                  {(formData.msdsFiles || []).map((file, index) => (
                    <div key={`${file.url || file.name}-${index}`} className="flex items-center justify-between gap-3 rounded-md border bg-white px-3 py-2">
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm text-blue-600 hover:underline truncate"
                      >
                        {file.name || `MSDS ${index + 1}`}
                      </a>
                      <button
                        type="button"
                        onClick={() => removeMsdsFile(index)}
                        className="text-gray-500 hover:text-red-600"
                        aria-label={`Remove ${file.name || `MSDS ${index + 1}`}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Insurance & Customs */}
      <div className="border-t pt-4">
        <h3 className="font-semibold text-lg mb-4">Insurance & Customs Value</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="flex items-center space-x-2 mb-2">
              <input
                type="checkbox"
                checked={formData.insuranceRequired}
                onChange={(e) => setFormData({ ...formData, insuranceRequired: e.target.checked })}
                className="rounded"
              />
              <span className="text-sm font-medium">Insurance Required</span>
            </label>
          </div>
          <div>
            <Label>Invoice Value (USD)</Label>
            <Input
              type="number"
              value={formData.customsValue}
              onChange={(e) => setFormData({ ...formData, customsValue: e.target.value })}
              placeholder="0.00"
            />
          </div>
        </div>
      </div>

    </form>
  );
};

export default LCLForm;
